import { useTranslation } from 'react-i18next';

import { StatusDot, type StatusTone } from '@/components/efs/StatusDot';
import { cn } from '@/lib/utils';
import {
  importFieldLabelKey,
  importValidationKey,
  isImportValidationCode,
  isImportValidationField,
} from '@/features/audit-issues';
import type { ValidationMessage, ValidationSeverity } from '../types';

interface ValidationMessageListProps {
  messages: ValidationMessage[];
  /** Source sheet name shared by these messages, when known (spec-13 §8.5). */
  sheet?: string | null;
  /** 1-based source row number shared by these messages, when known. */
  rowNumber?: number | null;
  className?: string;
  /** Called when a message row is activated (click, Enter, or Space) —
   * callers use this to focus/open the affected staging row/cell (spec-13 §8.5). */
  onSelect?: (msg: ValidationMessage) => void;
  /** Permission gate for raw parser code/message disclosure. */
  showTechnicalDetails?: boolean;
}

const severityToTone: Record<ValidationSeverity, StatusTone> = {
  info: 'info',
  warning: 'warning',
  error: 'error',
  critical: 'critical',
};

export const ValidationMessageList = ({
  messages,
  sheet,
  rowNumber,
  className,
  onSelect,
  showTechnicalDetails = false,
}: ValidationMessageListProps) => {
  const { t } = useTranslation();

  if (messages.length === 0) {
    return (
      <p className={cn('text-xs text-efs-text-muted', className)}>
        {t('efs.imports.review.messages_empty', { ns: 'imports' })}
      </p>
    );
  }

  // Shared row-level context (spec-13 §8.5): sheet/row are the same for
  // every message in this list; cell is per-message.
  const rowContextParts = [
    sheet ? `${t('efs.issues.import.sheet', { ns: 'issues' })}: ${sheet}` : null,
    rowNumber != null ? `${t('efs.issues.import.row', { ns: 'issues' })}: ${rowNumber}` : null,
  ].filter((part): part is string => !!part);

  return (
    <ul className={cn('space-y-2', className)}>
      {messages.map((msg, idx) => {
        // Primary message localization (spec-13 §8.5): cataloged codes use
        // their localized text; unknown codes use the generic fallback. Raw
        // parser codes/messages stay inside the system-admin disclosure only.
        const cataloged = isImportValidationCode(msg.code);
        const primaryText = cataloged
          ? t(importValidationKey(msg.code), { ns: 'issues' })
          : t(
              showTechnicalDetails
                ? 'efs.issues.import.unknown_code_technical'
                : 'efs.issues.import.unknown_code',
              { ns: 'issues' },
            );
        const cellPart = msg.cell
          ? `${t('efs.issues.import.cell', { ns: 'issues' })}: ${msg.cell}`
          : null;
        // Localized field label from the finite audited registry (spec-13
        // §8.5) — rendered before sheet/row/cell. An unregistered field key
        // never renders raw; it becomes the localized unavailable label and
        // the raw key stays inside the technical disclosure.
        const fieldPart = msg.field
          ? isImportValidationField(msg.field)
            ? t(importFieldLabelKey(msg.field), { ns: 'issues' })
            : t('efs.issues.import.field_unavailable', { ns: 'issues' })
          : null;
        const context = [fieldPart, ...rowContextParts, cellPart].filter(
          (part): part is string => !!part,
        );

        return (
          <li
            key={`${msg.code}-${idx}`}
            className="rounded-md border border-efs-border bg-efs-bg-panel text-sm"
          >
            <button
              type="button"
              className="flex min-h-11 w-full items-start gap-2 px-3 py-2 text-left"
              onClick={() => onSelect?.(msg)}
            >
              <StatusDot
                tone={severityToTone[msg.severity]}
                className="mt-1.5"
                label={t(`efs.imports.severities.${msg.severity}`, {
                  ns: 'imports',
                  defaultValue: msg.severity,
                })}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-efs-text-muted">
                    {t(`efs.imports.severities.${msg.severity}`, {
                      ns: 'imports',
                      defaultValue: msg.severity,
                    })}
                  </span>
                </div>
                {context.length > 0 ? (
                  <p className="mt-0.5 font-mono text-xs text-efs-text-muted">
                    {context.join(' / ')}
                  </p>
                ) : null}
                <p className="mt-1 text-sm text-efs-text-primary">{primaryText}</p>
              </div>
            </button>
            {showTechnicalDetails ? (
              <details className="px-3 pb-2">
                <summary className="min-h-6 cursor-pointer text-[11px] text-efs-text-muted hover:text-efs-text-primary">
                  {t('efs.issues.target.technical_details', { ns: 'issues' })}
                </summary>
                <p className="mt-1 whitespace-pre-wrap break-all rounded bg-efs-bg-subtle p-1.5 text-[11px] text-efs-text-muted">
                  {msg.code}
                  {msg.field ? ` [${msg.field}]` : ''}: {msg.message}
                </p>
              </details>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};
